import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from '@/components/ui/use-toast';
import { Language, TranslationContextType, LanguageOption } from './types';
import { fallbackTranslations } from './fallbackTranslations';
import { languageOptions } from './languageOptions';

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

export const TranslationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('en');
  const [translationCache, setTranslationCache] = useState<Record<string, Record<string, string>>>({});
  const [isTranslating, setIsTranslating] = useState(false);
  const [apiKeyFailed, setApiKeyFailed] = useState(false);
  
  useEffect(() => {
    const savedLanguage = localStorage.getItem('preferred-language');
    if (savedLanguage && ['en', 'rw', 'fr'].includes(savedLanguage)) {
      setLanguage(savedLanguage as Language);
    }
  }, []);
  
  useEffect(() => {
    localStorage.setItem('preferred-language', language);
  }, [language]);

  const translateText = async (text: string): Promise<string> => {
    if (language === 'en') return text;
    
    // First check if we have a cached translation
    if (translationCache[text]?.[language]) {
      return translationCache[text][language];
    }

    // Then check our fallback translations (from the language files)
    if (fallbackTranslations[language]?.[text]) {
      return fallbackTranslations[language][text];
    }
    
    // If API key already failed, just return the original text
    if (apiKeyFailed) {
      return text;
    }
    
    // Otherwise, try to translate using the API
    try {
      setIsTranslating(true);
      
      const apiKey = '';
      
      if (!apiKey) {
        setApiKeyFailed(true);
        throw new Error('No valid API key');
      }
      
      const targetLanguageName = languageOptions.find(option => option.code === language)?.name || language;
      
      const response = await fetch('https://generativelanguage.googleapis.com/v1beta/models/gemini-pro:generateContent?key=' + apiKey, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contents: [{
            parts: [{
              text: `Translate the following English text to ${targetLanguageName}. Return only the translated text, no explanations or additional content: "${text}"`
            }]
          }],
          generationConfig: {
            temperature: 0.2,
            topK: 40,
            topP: 0.95,
            maxOutputTokens: 1024,
          }
        }),
      });
      
      if (!response.ok) {
        console.error('Translation API error:', await response.text());
        setApiKeyFailed(true);
        
        if (fallbackTranslations[language]?.[text]) {
          return fallbackTranslations[language][text];
        }
        
        return text;
      }
      
      const data = await response.json();
      
      let translatedText = text;
      
      if (data.candidates && data.candidates.length > 0 && 
          data.candidates[0].content && 
          data.candidates[0].content.parts && 
          data.candidates[0].content.parts.length > 0) {
        translatedText = data.candidates[0].content.parts[0].text.trim();
        
        if (translatedText.startsWith('"') && translatedText.endsWith('"')) {
          translatedText = translatedText.slice(1, -1);
        }
        
        setTranslationCache(prev => ({
          ...prev,
          [text]: {
            ...prev[text],
            [language]: translatedText
          }
        }));
      }
      
      return translatedText;
    } catch (error) {
      console.error('Translation error:', error);
      
      setApiKeyFailed(true);
      
      // After API fails, check if we have a fallback
      if (fallbackTranslations[language]?.[text]) {
        return fallbackTranslations[language][text];
      }
      
      return text;
    } finally {
      setIsTranslating(false);
    }
  };

  return (
    <TranslationContext.Provider
      value={{
        language,
        setLanguage,
        translate: translateText,
        languageOptions,
        isTranslating
      }}
    >
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslation = () => {
  const context = useContext(TranslationContext);
  if (context === undefined) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
};
